import { Component, inject, Injector, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  TouchedChangeEvent,
} from "@angular/forms";
import { filter, map, startWith, Subject, takeUntil } from "rxjs";

import { OnChangeHandler, OnTouchedHandler } from "../../types/cva-callbacks";

@Component({
  selector: "app-abstract-control-accessor",
  template: "",
})
export abstract class AbstractControlAccessor<T>
  implements ControlValueAccessor, OnInit, OnDestroy
{
  protected abstract get control(): AbstractControl<Partial<T>, T>;

  protected readonly injector = inject(Injector);

  protected _ngControl: NgControl | null = null;
  public get ngControl(): NgControl | null {
    return this._ngControl;
  }

  protected get parentControl(): AbstractControl | undefined {
    return this.ngControl?.control;
  }

  protected readonly destroy = new Subject<void>();

  public ngOnInit(): void {
    // NgControl is injected here to avoid cyclic dependency in DI error
    if (!this._ngControl) {
      this._ngControl = this.injector.get(NgControl, null, {
        self: true,
        optional: true,
      });
    }
    // We have to wait until the next change detection cycle
    // before NgControl#control property is populated
    setTimeout(() => {
      this.watchParentControlTouchedChanges();
    });
  }

  /**
   * Controls that mirror the parent NgControl's touched state. Defaults to the
   * outward-facing control. Subclasses whose visible input is bound to a
   * different control (e.g. typeahead, where the inner input drives the error
   * border) can widen this so those controls get touched too.
   */
  protected get touchedStateTargets(): AbstractControl[] {
    return [this.control];
  }

  private watchParentControlTouchedChanges(): void {
    this.parentControl?.events
      .pipe(
        filter((event) => event instanceof TouchedChangeEvent),
        map((event) => event.touched),
        startWith(this.parentControl.touched),
        takeUntil(this.destroy)
      )
      .subscribe((parentControlTouched) => {
        for (const target of this.touchedStateTargets) {
          if (parentControlTouched === target.touched) continue;

          if (parentControlTouched) {
            target.markAllAsTouched();
          } else {
            target.markAsUntouched();
          }
        }
      });
  }

  //#region CVA implementation
  public writeValue(value: T | null): void {
    if (value) {
      this.control.setValue(value, { emitEvent: false });
    } else {
      this.control.reset();
    }
  }

  public registerOnChange(onChangeHandler: OnChangeHandler<T | null>): void {
    this.control.valueChanges
      .pipe(
        map(() => this.control.value),
        takeUntil(this.destroy)
      )
      .subscribe(onChangeHandler);
  }

  public registerOnTouched(onTouchedHandler: OnTouchedHandler): void {
    this.control.events
      .pipe(
        filter((event) => event instanceof TouchedChangeEvent),
        filter((event) => event.touched),
        takeUntil(this.destroy)
      )
      .subscribe(onTouchedHandler);
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }
  //#endregion CVA implementation

  /**
   * Don't rewrite using takeUntilDestroyed(this.destroyRef), at least yet!
   * Inside CVA methods it causes NG0911 'View has already been destroy' error.
   */
  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
